/**
 * Navigate to the in-person report edit view or a group's report list.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@/components/ui/toast';
import { getReports } from '@/services/inperson/reports.service';
import { getErrorMessage } from '@/utils/errors-utils';

/** Open the in-person session-report edit view for a session. */
export const useNavigateToReport = () => {
  const navigate = useNavigate();
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);

  const { data: reports, isLoading } = useQuery({
    queryKey: ['session-report', pendingSessionId],
    queryFn: () => getReports(pendingSessionId!),
    enabled: !!pendingSessionId,
    retry: 1,
  });

  useEffect(() => {
    if (!pendingSessionId || isLoading) return;

    if (reports?.length && reports.length > 0) {
      const report = reports[0];
      void navigate('/reports', {
        state: {
          editReport: { sessionId: pendingSessionId, reportId: report.id },
        },
      });
      setPendingSessionId(null);
    } else if (reports?.length === 0) {
      toast.info(getErrorMessage('noReportForSession'));
      setPendingSessionId(null);
    }
  }, [reports, isLoading, pendingSessionId, navigate]);

  const navigateToReport = useCallback((sessionId: string) => {
    setPendingSessionId(sessionId);
  }, []);

  const navigateToGroupReports = useCallback(
    (groupId: string, label?: string) => {
      void navigate('/reports', {
        state: { reportsForGroup: { groupId, label } },
      });
    },
    [navigate],
  );

  return { navigateToReport, navigateToGroupReports, isLoading };
};
